import type { Theme } from '../settings/themes.js';

/** Canvas palette every renderer reads, derived from the active theme. */
export interface RenderPalette {
  readonly name: string;
  readonly borderWoodBase: string;
  readonly borderWoodDark: string;
  readonly borderWoodLight: string;
  readonly boardWoodBase: string;
  readonly boardWoodDark: string;
  readonly boardWoodLight: string;
  readonly darkSquareBase: string;
  readonly darkSquareDeep: string;
  readonly lightSquareBase: string;
  readonly lightSquareDeep: string;
  readonly labelColor: string;
  readonly innerShadow: string;
  readonly innerHighlight: string;
  readonly whitePieceBase: string;
  readonly whitePieceHighlight: string;
  readonly whitePieceShadow: string;
  readonly whitePieceBorder: string;
  readonly blackPieceBase: string;
  readonly blackPieceHighlight: string;
  readonly blackPieceShadow: string;
  readonly blackPieceBorder: string;
  readonly hoverGlow: string;
  readonly selectedRing: string;
  /** Legal-move dot or capture ring on a light square… */
  readonly legalDot: string;
  /** …and on a dark one, where a dark dot would disappear. */
  readonly legalDotOnDark: string;
  readonly lastMoveTint: string;
  readonly checkGlow: string;
  readonly focusRing: string;
  readonly bannerBg: string;
  readonly bannerWinBg: string;
  readonly bannerDrawBg: string;
  readonly bannerBorder: string;
  readonly bannerTextColor: string;
}

/**
 * Semantic colours (legal-move dots, check glow) stay constant across themes —
 * they encode game state, not chrome.
 */
export function themeToRenderPalette(theme: Theme): RenderPalette {
  return {
    name: theme.name,
    borderWoodBase: theme.borderWoodBase,
    borderWoodDark: theme.borderWoodDark,
    borderWoodLight: theme.borderWoodLight,
    boardWoodBase: theme.boardWoodBase,
    boardWoodDark: theme.boardWoodDark,
    boardWoodLight: theme.boardWoodLight,
    darkSquareBase: theme.darkSquareBase,
    darkSquareDeep: theme.darkSquareTip,
    lightSquareBase: theme.lightSquareBase,
    lightSquareDeep: theme.lightSquareTip,
    labelColor: theme.inkSoft,
    innerShadow: 'rgba(0, 0, 0, 0.45)',
    innerHighlight: 'rgba(255, 220, 160, 0.18)',
    whitePieceBase: theme.whiteCheckerColor,
    whitePieceHighlight: theme.whiteCheckerHighlight,
    whitePieceShadow: theme.whiteCheckerShadow,
    whitePieceBorder: theme.whiteCheckerBorder,
    blackPieceBase: theme.blackCheckerColor,
    blackPieceHighlight: theme.blackCheckerHighlight,
    blackPieceShadow: theme.blackCheckerShadow,
    blackPieceBorder: theme.blackCheckerBorder,
    hoverGlow: theme.hoverGlowColor,
    selectedRing: theme.brassHi,
    legalDot: 'rgba(20, 12, 6, 0.38)',
    legalDotOnDark: 'rgba(244, 226, 190, 0.5)',
    lastMoveTint: theme.lastMoveTint,
    checkGlow: 'rgba(230, 40, 30, 0.85)',
    focusRing: theme.brassHi,
    bannerBg: theme.bannerBg,
    bannerWinBg: theme.bannerWinBg,
    bannerDrawBg: 'rgba(80, 80, 90, 0.9)',
    bannerBorder: theme.bannerBorder,
    bannerTextColor: theme.bannerTextColor,
  };
}
